import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LANGS } from "@/i18n/strings";

/**
 * Unified language switcher — single dropdown that drives BOTH:
 *  • the in-app i18n strings (English / Hindi / Hinglish) via useI18n()
 *  • the page-wide Google Translate widget (20 regional + world languages)
 *
 * Sections in the dropdown:
 *  1. "App language" — Hinglish / English / Hindi (instant, no reload)
 *  2. "Translate page" — all other Indian + world languages (Google Translate,
 *     requires a single page reload)
 *
 * This replaces the previous two-selectors-side-by-side that caused
 * navbar overlap. Default = Hinglish.
 */
const TRANSLATE_LANGUAGES = [
  // Indian regional (skipping en + hi which live in the App section)
  { code: "bn",    label: "Bengali",    native: "বাংলা" },
  { code: "mr",    label: "Marathi",    native: "मराठी" },
  { code: "ta",    label: "Tamil",      native: "தமிழ்" },
  { code: "te",    label: "Telugu",     native: "తెలుగు" },
  { code: "gu",    label: "Gujarati",   native: "ગુજરાતી" },
  { code: "kn",    label: "Kannada",    native: "ಕನ್ನಡ" },
  { code: "ml",    label: "Malayalam",  native: "മലയാളം" },
  { code: "pa",    label: "Punjabi",    native: "ਪੰਜਾਬੀ" },
  { code: "ur",    label: "Urdu",       native: "اردو" },
  { code: "ar",    label: "Arabic",     native: "العربية" },
  { code: "fr",    label: "French",     native: "Français" },
  { code: "es",    label: "Spanish",    native: "Español" },
  { code: "de",    label: "German",     native: "Deutsch" },
  { code: "zh-CN", label: "Chinese",    native: "中文" },
  { code: "ja",    label: "Japanese",   native: "日本語" },
  { code: "ru",    label: "Russian",    native: "Русский" },
  { code: "pt",    label: "Portuguese", native: "Português" },
  { code: "id",    label: "Indonesian", native: "Bahasa Indonesia" },
];

function readGoogTransCookie() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=\/([^/]+)\/([^;]+)/);
  return (m && m[2]) || "en";
}

function applyGoogleTranslate(code) {
  // Setting the cookie + reload is the standard Google Translate pattern.
  const value = code === "en" ? "/auto/en" : `/auto/${code}`;
  const host = window.location.hostname;
  document.cookie = `googtrans=${value}; path=/`;
  document.cookie = `googtrans=${value}; path=/; domain=${host}`;
  const parts = host.split(".");
  if (parts.length > 2) {
    const parent = "." + parts.slice(-2).join(".");
    document.cookie = `googtrans=${value}; path=/; domain=${parent}`;
  }
  window.location.reload();
}

export default function UnifiedLanguageSwitcher({ compact = false }) {
  const { lang, setLang } = useI18n();
  const [translateLang, setTranslateLang] = useState("en");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setTranslateLang(readGoogTransCookie()); }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const currentApp = LANGS.find((l) => l.code === lang) || LANGS[0];
  const currentTr  = TRANSLATE_LANGUAGES.find((l) => l.code === translateLang);
  // Button label: show the app language. Add a "+" badge if Google Translate is
  // also active to a different regional/world language.
  const showBadge = !!currentTr && translateLang !== "en" && translateLang !== "hi";

  return (
    <div ref={ref} className="relative notranslate" translate="no" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-md text-sm font-semibold transition-colors border border-slate-200 bg-white hover:border-slate-300 ${
          compact ? "h-9 px-2" : "h-9 px-3"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-btn"
      >
        <Globe size={15} className="text-slate-900" />
        <span className="text-slate-700">{compact ? currentApp.short : currentApp.label}</span>
        {showBadge && (
          <span className="text-[9px] uppercase font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded">
            +{currentTr.code.toUpperCase()}
          </span>
        )}
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1.5 w-72 max-h-[460px] overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-2xl z-50 py-2"
          data-testid="lang-switcher-menu"
        >
          {/* Section 1 — App language */}
          <div className="px-3 pb-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">App language (instant)</div>
          </div>
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={lang === l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${lang === l.code ? "bg-slate-50 text-slate-900 font-semibold" : "text-slate-700"}`}
              data-testid={`lang-app-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{l.flag}</span>
                <span className="font-medium">{l.label}</span>
                <span className="text-[10px] text-slate-400 font-mono">{l.short}</span>
              </span>
              {lang === l.code && <Check size={14} className="text-slate-900 shrink-0" />}
            </button>
          ))}

          {/* Section 2 — Google Translate */}
          <div className="border-t border-slate-100 mt-1.5 pt-2 px-3 pb-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Translate page (Google · reload)</div>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); applyGoogleTranslate("en"); }}
            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${translateLang === "en" ? "bg-slate-50 text-slate-900 font-semibold" : "text-slate-700"}`}
            data-testid="lang-translate-en"
          >
            <span>English (no translation)</span>
            {translateLang === "en" && <Check size={14} className="text-slate-900 shrink-0" />}
          </button>
          {TRANSLATE_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={translateLang === l.code}
              onClick={() => { setOpen(false); applyGoogleTranslate(l.code); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${translateLang === l.code ? "bg-slate-50 text-slate-900 font-semibold" : "text-slate-700"}`}
              data-testid={`lang-translate-${l.code}`}
            >
              <span>
                <span className="font-medium">{l.native}</span>
                {l.label !== l.native && <span className="text-slate-500 text-xs ml-1.5">· {l.label}</span>}
              </span>
              {translateLang === l.code && <Check size={14} className="text-slate-900 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
